let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let dataArray: Uint8Array<ArrayBuffer> | null = null;
let sourceNode: MediaStreamAudioSourceNode | null = null;
let activeStream: MediaStream | null = null;

let resumePending = false;
let analysisSuspended = false;

const registeredElements = new WeakSet<HTMLMediaElement>();
/** Strong refs so we can re-attach analysis after background suspend. */
const registeredElementList = new Set<HTMLMediaElement>();

/**
 * iOS/iPadOS (all browsers are WebKit) mutes an <audio> element's output once its
 * live MediaStream is routed through `createMediaStreamSource`. The element is our
 * actual playback sink for live WebRTC, so on iOS we must NOT tap the live stream
 * for analysis — doing so would silence the broadcast. iPadOS reports as MacIntel,
 * so detect it via touch points too.
 */
function isWebKitMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent ?? "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
}

export function teardownAudioContext(): void {
  if (sourceNode) {
    try {
      sourceNode.disconnect();
    } catch {
      /* ignore */
    }
    sourceNode = null;
  }
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
  analyser = null;
  dataArray = null;
  activeStream = null;
  resumePending = false;
}

function ensureAnalyser(): void {
  if (audioContext && audioContext.state !== "closed" && analyser && dataArray) {
    return;
  }
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  audioContext = new Ctx();
  analyser = audioContext.createAnalyser();
  analyser.fftSize = 64;
  dataArray = new Uint8Array(analyser.frequencyBinCount);
}

function attachStreamToAnalyser(stream: MediaStream, options?: { resetContext?: boolean }): void {
  if (analysisSuspended) return;
  if (!stream || stream.getAudioTracks().length === 0) return;
  if (activeStream === stream && sourceNode) return;

  if (options?.resetContext) {
    teardownAudioContext();
  }
  ensureAnalyser();

  if (sourceNode) {
    try {
      sourceNode.disconnect();
    } catch {
      /* ignore */
    }
    sourceNode = null;
  }

  activeStream = stream;
  sourceNode = audioContext!.createMediaStreamSource(stream);
  sourceNode.connect(analyser!);

  if (audioContext!.state === "suspended") {
    void audioContext!.resume();
  }
}

function captureStreamFromElement(element: HTMLMediaElement): MediaStream | null {
  const el = element as HTMLMediaElement & {
    captureStream?: () => MediaStream;
    mozCaptureStream?: () => MediaStream;
  };
  if (typeof el.captureStream === "function") return el.captureStream();
  if (typeof el.mozCaptureStream === "function") return el.mozCaptureStream();
  return null;
}

function attachElementCaptureStream(element: HTMLMediaElement): void {
  if (analysisSuspended) return;
  try {
    const stream = captureStreamFromElement(element);
    if (!stream || stream.getAudioTracks().length === 0) return;
    attachStreamToAnalyser(stream, { resetContext: false });
  } catch (e) {
    console.warn("[Riq Visualizer] captureStream attach failed:", e);
  }
}

/**
 * Hook analyser to an HTMLMediaElement via captureStream (station / library playback).
 * Safe to call once per element; registers a play listener for subsequent sources.
 */
export function registerMediaElementForAnalysis(element: HTMLMediaElement): void {
  if (!element || registeredElements.has(element)) return;
  registeredElements.add(element);
  registeredElementList.add(element);

  const attach = () => attachElementCaptureStream(element);
  // `play` can fire before decode is ready; `playing` / `loadeddata` retry captureStream.
  element.addEventListener("play", attach);
  element.addEventListener("playing", attach);
  element.addEventListener("loadeddata", attach);
  if (!element.paused && element.readyState >= 2) {
    attach();
  }
}

/** Hook analyser to a live MediaStream (mic / WebRTC). */
export function registerAudioStreamForAnalysis(stream: MediaStream): void {
  if (!stream || analysisSuspended) return;
  // On iOS/WebKit, tapping a remote WebRTC stream silences the <audio> sink.
  // Skip analysis there; playback is more important than visualizer bars.
  if (isWebKitMobile()) return;
  attachStreamToAnalyser(stream, { resetContext: true });
}

/** Returns normalized bar heights (0–1) for vocal-frequency bins. */
export function getAudioVisualizerHeights(barCount: number): number[] | null {
  if (!analyser || !dataArray) return null;

  if (audioContext?.state === "suspended" && !resumePending) {
    resumePending = true;
    void audioContext.resume().finally(() => {
      resumePending = false;
    });
  }

  try {
    analyser.getByteFrequencyData(dataArray);
  } catch {
    return null;
  }

  const levels: number[] = [];
  const maxVocalBin = 7;

  for (let i = 0; i < barCount; i++) {
    const percent = i / barCount;
    const binIndex = Math.floor(percent * maxVocalBin);
    let value = dataArray[binIndex]! / 255;

    if (binIndex === 0) {
      value *= 0.85;
    } else if (binIndex === 1 || binIndex === 2) {
      value *= 1.08;
    } else if (binIndex === 3 || binIndex === 4) {
      value *= 1.04;
    }

    levels.push(Math.min(1, Math.max(0, value)));
  }
  return levels;
}

export async function resumeAudioContext(): Promise<void> {
  if (!audioContext || audioContext.state === "closed") return;
  if (audioContext.state === "suspended") {
    try {
      await audioContext.resume();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Detach Web Audio analysis while the page is backgrounded so `<audio>` playback
 * is not competing with a suspended `AudioContext` (Android/Capacitor, mobile Chrome).
 */
export function suspendMediaElementAnalysis(): void {
  analysisSuspended = true;
  teardownAudioContext();
}

/** Re-attach analysis after returning to the foreground. */
export function resumeMediaElementAnalysis(): void {
  if (!analysisSuspended) return;
  analysisSuspended = false;
  for (const element of registeredElementList) {
    if (!element.paused && element.readyState >= 2) {
      attachElementCaptureStream(element);
    }
  }
}

/** Test hook. */
export function resetMediaAnalysisStateForTests(): void {
  analysisSuspended = false;
  registeredElementList.clear();
  teardownAudioContext();
}
